import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { CalendarDays, ChevronLeft, ChevronRight, Filter, Users, AlertTriangle, Clock, Phone, Stethoscope, Sparkles, X } from 'lucide-react';
import { theme, cardStyle, inputStyle } from '../theme';
import { STATUS_CONFIG, CAL_SLOTS, CAL_SLOTS_15 } from '../constants';
import WaitListPanel from '../components/WaitListPanel';
import SmartFindPanel from '../components/SmartFindPanel';

// Hatched diagonal-stripe pattern for slots outside working hours.
const CLOSED_STRIPES = 'repeating-linear-gradient(-45deg, transparent 0 6px, rgba(15,23,42,0.06) 6px 7px)';

// 'sun','mon',... index aligned with Date.getDay()
const DAY_KEYS = ['sun','mon','tue','wed','thu','fri','sat'];

// Fallback used when the primary branch has no `hours` field saved yet (fresh
// install). Mirrors ClinicHoursEditor's DEFAULT_HOURS so the calendar's
// hatched-pattern view is visible immediately, before the user clicks Save.
const DEFAULT_BRANCH_HOURS = {
  sun: { open: '',      close: '',      isClosed: true  },
  mon: { open: '09:00', close: '20:00', isClosed: false },
  tue: { open: '09:00', close: '20:00', isClosed: false },
  wed: { open: '09:00', close: '20:00', isClosed: false },
  thu: { open: '09:00', close: '20:00', isClosed: false },
  fri: { open: '09:00', close: '20:00', isClosed: false },
  sat: { open: '09:00', close: '14:00', isClosed: false },
};

// Branch hours come from /branches → primaryBranch.hours, edited in
// Settings → Calendar Settings → ClinicHoursEditor. Treat anything outside
// the day's open/close window (or full-day closed) as a non-working slot.
const isWorkingSlot = (dateISO, hhmm, hours) => {
  if (!hours || !dateISO || !hhmm) return true; // unknown → treat as working
  const d = new Date(dateISO + 'T00:00:00');
  const day = hours[DAY_KEYS[d.getDay()]];
  if (!day || day.isClosed) return false;
  if (!day.open || !day.close) return true;
  return hhmm >= day.open && hhmm < day.close;
};

// ═══════════════════════════════════════════════════════════════════
//  CALENDAR PAGE — v2 redesign (April 2026)
//  Inspired by BestoSys-style dense week view:
//   • Status counter pills with live counts
//   • Redesigned appointment cards (uppercase name + italic doctor + time)
//   • Compact/Dense mode toggle (30-min ↔ 15-min grid)
//   • "Today" sidebar list with quick-action buttons per row
//   • Check-in warning banner for scheduled patients not yet arrived
//   • Collapsible doctor filter retained
// ═══════════════════════════════════════════════════════════════════

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

// Util: YYYY-MM-DD from a Date object (local timezone, not UTC)
const toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};

// Snap any HH:mm to a slot key. Precision=30 → :00/:30, Precision=15 → :00/:15/:30/:45
const snapSlot = (time='00:00', precision=30) => {
  const [h, m] = time.split(':').map(Number);
  if (precision === 15) {
    const q = m < 15 ? 0 : m < 30 ? 15 : m < 45 ? 30 : 45;
    return `${String(h).padStart(2,'0')}:${String(q).padStart(2,'0')}`;
  }
  return `${String(h).padStart(2,'0')}:${m < 30 ? '00' : '30'}`;
};

// Format h:mm AM/PM for card display (e.g. "12:15p")
const fmtShortTime = (hhmm='') => {
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h)) return hhmm;
  const suffix = h >= 12 ? 'p' : 'a';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2,'0')}${suffix}`;
};

const CalendarPage = ({ app = {} }) => {
  const {
    API_BASE,
    appointmentsList = [],
    fetchAppointments,
    selAppt, setSelAppt,
    calView = 'day', setCalView,
    calDayDate, setCalDayDate,
    calWeekStart, setCalWeekStart,
    calMonth, setCalMonth, calYear, setCalYear,
    selectedDate, setSelectedDate,
    setSlotClick,
    setCalBookForm,
    patientsList = [],
    treatmentTypes = [],
    branches = [],
  } = app;

  // Primary branch's opening hours (set in Settings → Calendar Settings).
  // Slots outside the day's open/close window render with a hatched pattern.
  // Falls back to DEFAULT_BRANCH_HOURS so a fresh install (no save yet) still
  // shows Sunday closed + Saturday half-day, instead of looking unconfigured.
  const branchHours = useMemo(() => {
    if (!Array.isArray(branches) || branches.length === 0) return DEFAULT_BRANCH_HOURS;
    const primary = branches.find(b => b.isPrimary) || branches[0];
    const saved = primary?.hours;
    const hasAnyDay = saved && Object.keys(saved).some(k => DAY_KEYS.includes(k) && saved[k]);
    return hasAnyDay ? saved : DEFAULT_BRANCH_HOURS;
  }, [branches]);

  // ── Local UI state ──
  const [showWaitList, setShowWaitList] = useState(false);
  const [showSmartFind, setShowSmartFind] = useState(false);
  const [doctorFilter, setDoctorFilter] = useState('All Doctors');
  const [doctorOptions, setDoctorOptions] = useState(['All Doctors']);
  const [compactMode, setCompactMode] = useState(() => {
    try { return localStorage.getItem('cal.compact') === '1'; } catch { return false; }
  });
  const [denseGrid, setDenseGrid] = useState(() => {
    try { return localStorage.getItem('cal.dense15.v3') === '1'; } catch { return false; }
  });

  // Persist UI prefs so Calendar re-opens in the same state.
  useEffect(() => { try { localStorage.setItem('cal.compact', compactMode ? '1' : '0'); } catch {} }, [compactMode]);
  useEffect(() => { try { localStorage.setItem('cal.dense15.v3', denseGrid ? '1' : '0'); } catch {} }, [denseGrid]);

  const slots = denseGrid ? CAL_SLOTS_15 : CAL_SLOTS;
  const slotPrecision = denseGrid ? 15 : 30;
  // Tight BestoSys-style density — 15-min bands stay visually distinct but
  // don't hog vertical space. Compact mode squeezes them further.
  const rowHeight = compactMode
    ? (denseGrid ? 16 : 22)
    : (denseGrid ? 24 : 32);

  // ── Today reference ──
  const today = useMemo(() => new Date(), []);
  const todayStr = toISO(today);
  const nowSlot = snapSlot(`${today.getHours()}:${today.getMinutes()}`, slotPrecision);

  // Scroll the day/week timeline so the current-time slot lands at row 3 from
  // the top of the scrollable area whenever today is visible. Re-runs when
  // grid density / view / date changes.
  const timelineScrollerRef = useRef(null);
  useEffect(() => {
    const el = timelineScrollerRef.current;
    if (!el) return;
    const idx = slots.indexOf(nowSlot);
    if (idx < 0) { el.scrollTop = 0; return; }
    el.scrollTop = Math.max(0, (idx - 2) * rowHeight);
  }, [calView, calDayDate, calWeekStart, nowSlot, rowHeight, slots]);

  // ── Fetch doctors for filter ──
  useEffect(() => {
    if (!API_BASE) return;
    axios.get(`${API_BASE}/doctors`)
      .then(res => {
        const names = (res.data || []).map(d => d.name || d.fullName).filter(Boolean);
        setDoctorOptions(['All Doctors', ...names]);
      })
      .catch(() => setDoctorOptions(['All Doctors']));
  }, [API_BASE]);

  // ── Filter + index appointments ──
  const filtered = useMemo(() => (
    doctorFilter === 'All Doctors'
      ? appointmentsList
      : appointmentsList.filter(a => (a.doctor || a.doctorName || '').toLowerCase().includes(doctorFilter.toLowerCase()))
  ), [appointmentsList, doctorFilter]);

  const apptsByDate = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      if (!a?.date) return;
      (map[a.date] = map[a.date] || []).push(a);
    });
    return map;
  }, [filtered]);

  const slotMap = useMemo(() => {
    const map = {};
    filtered.forEach(a => {
      if (!a?.date || !a?.time) return;
      const key = snapSlot(a.time, slotPrecision);
      const day = (map[a.date] = map[a.date] || {});
      (day[key] = day[key] || []).push(a);
    });
    return map;
  }, [filtered, slotPrecision]);

  // ── Today's appointment roster (used by sidebar + warning banner) ──
  const todayAppts = useMemo(() => {
    const list = (apptsByDate[todayStr] || []).slice();
    list.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    return list;
  }, [apptsByDate, todayStr]);

  const todayCounts = useMemo(() => {
    const c = { total: todayAppts.length, Scheduled: 0, 'PT In': 0, Engage: 0, 'PT Out': 0, Cancelled: 0 };
    todayAppts.forEach(a => { if (c[a.status] != null) c[a.status]++; });
    return c;
  }, [todayAppts]);

  // "Has not checked in yet" = scheduled for today but the slot time has already passed
  const notCheckedIn = useMemo(() => {
    const nowH = today.getHours(), nowM = today.getMinutes();
    return todayAppts.filter(a => {
      if (a.status !== 'Scheduled') return false;
      const [h, m] = (a.time || '').split(':').map(Number);
      if (isNaN(h)) return false;
      return h < nowH || (h === nowH && m <= nowM);
    });
  }, [todayAppts, today]);

  // ─────────────────────────────────────────────
  //  CLICK HANDLERS
  // ─────────────────────────────────────────────

  const openBookingForSlot = useCallback((date, time) => {
    if (typeof setSlotClick !== 'function') {
      console.error('[Calendar] setSlotClick prop missing');
      alert('Booking modal wiring broken. Refresh karo.');
      return;
    }
    setSelAppt && setSelAppt(null);
    setCalBookForm && setCalBookForm(f => ({ ...(f || {}), time }));
    setSlotClick({ date, time });
  }, [setSlotClick, setSelAppt, setCalBookForm]);

  const showApptDetails = useCallback((e, appt) => {
    if (e) e.stopPropagation();
    setSlotClick && setSlotClick(null);
    setSelAppt && setSelAppt(appt);
  }, [setSlotClick, setSelAppt]);

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_BASE}/appointments/status/${id}`, { status });
      setSelAppt && setSelAppt(prev => (prev && prev._id === id ? { ...prev, status } : prev));
      fetchAppointments && fetchAppointments();
    } catch (err) {
      alert('Status update failed: ' + (err.response?.data?.error || err.message));
    }
  };

  // Picked a slot from Smart Find — jump the calendar to that date and open
  // the booking modal pre-filled. Uses the same setSlotClick/setCalBookForm
  // wiring as manual slot-click so no new backend endpoints are touched.
  const handleSmartPick = useCallback((s) => {
    setShowSmartFind(false);
    setCalDayDate && setCalDayDate(s.date);
    if (calView !== 'day' && setCalView) setCalView('day');
    setCalBookForm && setCalBookForm(f => ({
      ...(f || {}),
      time: s.time,
      treatment: s.treatment || (f && f.treatment) || '',
      doctor: s.doctorName || '',
      doctorId: s.doctorId || undefined,
      duration: s.duration || (f && f.duration) || 30,
    }));
    setSlotClick && setSlotClick({ date: s.date, time: s.time });
  }, [setCalDayDate, calView, setCalView, setCalBookForm, setSlotClick]);

  // Quick-advance status from sidebar rows (Sch → PT In → Engage → PT Out)
  const quickAdvance = async (appt, e) => {
    if (e) e.stopPropagation();
    const flow = { 'Scheduled': 'PT In', 'PT In': 'Engage', 'Engage': 'PT Out' };
    const next = flow[appt.status];
    if (!next) return;
    await changeStatus(appt._id, next);
  };

  // ─────────────────────────────────────────────
  //  APPOINTMENT CARD — compact BestoSys-style chip
  // ─────────────────────────────────────────────
  const ApptCard = ({ a }) => {
    const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG['Scheduled'];
    const doctorName = a.doctor || a.doctorName || '';
    const duration = a.duration ? `${a.duration}mins` : '30mins';
    return (
      <div
        onClick={(e) => showApptDetails(e, a)}
        title={`${a.patientName} • ${a.time} • ${a.status}`}
        style={{
          padding: compactMode ? '2px 6px' : '3px 7px',
          borderRadius: 5,
          fontSize: compactMode ? '0.66rem' : '0.72rem',
          cursor: 'pointer',
          marginBottom: 2,
          background: `${cfg.color}22`,
          borderLeft: `3px solid ${cfg.color}`,
          color: '#1f2d3d',
          overflow: 'hidden',
          lineHeight: 1.25,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}>
        <div style={{
          fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {cfg.icon} {a.patientName}
        </div>
        {!compactMode && doctorName && (
          <div style={{ fontStyle: 'italic', color: '#6c757d', fontSize: '0.66rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {doctorName}
          </div>
        )}
        {!compactMode && (
          <div style={{ color: '#6c757d', fontSize: '0.64rem' }}>
            {fmtShortTime(a.time)} · {duration}
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────
  //  TOP TOOLBAR — view tabs, wait-list, doctor filter, mode toggles
  // ─────────────────────────────────────────────
  const Toolbar = () => {
    const tabBtn = (active) => ({
      padding: '9px 22px',
      borderRadius: '10px',
      border: 'none',
      cursor: 'pointer',
      fontWeight: 700,
      fontSize: '0.92rem',
      transition: 'all 0.18s',
      backgroundColor: active ? theme.primary : '#eef2f5',
      color: active ? 'white' : '#5a6470',
      boxShadow: active ? `0 4px 12px ${theme.primary}44` : 'none',
    });

    const toggle = (on, label, onClick, title) => (
      <button onClick={onClick} title={title}
        style={{
          padding: '7px 12px', borderRadius: 8, fontSize: '0.8rem', fontWeight: 700,
          border: `1.5px solid ${on ? theme.primary : '#dee2e6'}`,
          background: on ? `${theme.primary}11` : 'white',
          color: on ? theme.primaryDark : '#6c757d',
          cursor: 'pointer', transition: '0.15s',
        }}>
        {on ? '● ' : '○ '}{label}
      </button>
    );

    return (
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '10px',
        marginBottom: '20px', padding: '14px 18px', borderRadius: '14px',
        background: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>
        {[['day','📅 Day'], ['week','📆 Week'], ['month','🗓️ Month']].map(([v, lbl]) => (
          <button key={v} onClick={() => { setCalView && setCalView(v); setShowWaitList(false); setSelAppt && setSelAppt(null); }}
            style={tabBtn(calView === v && !showWaitList)}>
            {lbl}
          </button>
        ))}

        <button onClick={() => setShowWaitList(w => !w)} style={tabBtn(showWaitList)}>
          <Users size={14} style={{display:'inline', marginRight:6, verticalAlign:'-2px'}}/>
          Wait List
        </button>

        <button onClick={() => setShowSmartFind(true)} style={tabBtn(showSmartFind)}
          title="AI-rank best slots based on doctor schedule, duration and preferences">
          <Sparkles size={14} style={{display:'inline', marginRight:6, verticalAlign:'-2px'}}/>
          Smart Find
        </button>

        {!showWaitList && (
          <>
            {toggle(compactMode, 'Compact', () => setCompactMode(v => !v), 'Reduce row height to fit more slots on screen')}
            {toggle(denseGrid,   '15-min',  () => setDenseGrid(v => !v),    'Use 15-min grid instead of 30-min')}
          </>
        )}

        <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:'8px'}}>
          <Filter size={15} style={{color:'#999'}}/>
          <select value={doctorFilter} onChange={e => setDoctorFilter(e.target.value)}
            style={{
              ...inputStyle, width: 'auto', minWidth: '170px',
              padding: '8px 12px', fontSize: '0.88rem', fontWeight: 600,
              borderColor: doctorFilter !== 'All Doctors' ? theme.primary : '#dee2e6',
              color: doctorFilter !== 'All Doctors' ? theme.primaryDark : '#333',
              cursor: 'pointer',
            }}>
            {doctorOptions.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
          {doctorFilter !== 'All Doctors' && (
            <button onClick={() => setDoctorFilter('All Doctors')} title="Clear filter"
              style={{background:'none', border:'none', cursor:'pointer', fontSize:'1rem', color:'#aaa'}}>✕</button>
          )}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────
  //  STATUS PILLS — BestoSys-style counter row
  // ─────────────────────────────────────────────
  const StatusPills = () => {
    const pills = [
      { key: 'total', label: 'Total', count: todayCounts.total, bg: theme.navy, fg: 'white' },
      ...Object.entries(STATUS_CONFIG).map(([s, cfg]) => ({
        key: s,
        label: s === 'Scheduled' ? 'Sch' : s === 'PT In' ? 'In' : s === 'Engage' ? 'Eng' : s === 'PT Out' ? 'Out' : 'Canc',
        count: todayCounts[s] || 0,
        bg: cfg.color,
        fg: 'white',
      })),
    ];
    return (
      <div style={{display:'grid', gridTemplateColumns:'repeat(6, 1fr)', gap:6, marginBottom:14}}>
        {pills.map(p => (
          <div key={p.key} style={{
            padding:'8px 4px', borderRadius:9, textAlign:'center',
            background:p.bg, color:p.fg, fontWeight:700,
            boxShadow:'0 2px 6px rgba(0,0,0,0.08)',
          }}>
            <div style={{fontSize:'1.15rem', lineHeight:1}}>{p.count}</div>
            <div style={{fontSize:'0.64rem', marginTop:3, opacity:0.95, letterSpacing:'0.03em'}}>{p.label}</div>
          </div>
        ))}
      </div>
    );
  };

  // ─────────────────────────────────────────────
  //  TODAY SIDEBAR LIST — patients with quick actions
  // ─────────────────────────────────────────────
  const TodayList = () => {
    if (!todayAppts.length) {
      return (
        <div style={{textAlign:'center', color:'#bbb', padding:'30px 12px', fontSize:'0.85rem'}}>
          <CalendarDays size={32} style={{marginBottom:8, opacity:0.35}}/>
          <p style={{margin:0, fontWeight:600, color:'#999'}}>Aaj ka koi appointment nahi</p>
          <p style={{margin:'4px 0 0', fontSize:'0.74rem', color:'#bbb'}}>Slot pe click → naya booking</p>
        </div>
      );
    }

    const flowLabel = { 'Scheduled': 'In', 'PT In': 'Eng', 'Engage': 'Out' };

    return (
      <div style={{display:'flex', flexDirection:'column', gap:8}}>
        {todayAppts.map(a => {
          const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG['Scheduled'];
          const hasNext = !!flowLabel[a.status];
          const isNotCheckedIn = notCheckedIn.some(x => x._id === a._id);
          return (
            <div key={a._id} onClick={(e) => showApptDetails(e, a)}
              style={{
                padding:'9px 10px', borderRadius:9, cursor:'pointer',
                background: isNotCheckedIn ? '#fff8e1' : '#f8f9fb',
                borderLeft:`4px solid ${cfg.color}`,
                transition:'0.15s',
              }}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:6}}>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:700, fontSize:'0.86rem', color:'#1f2d3d',
                               textTransform:'uppercase', letterSpacing:'0.02em',
                               whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                    {a.patientName}
                  </div>
                  <div style={{display:'flex', alignItems:'center', gap:6, fontSize:'0.72rem', color:'#6c757d', marginTop:2}}>
                    <Clock size={11}/>
                    <span>{fmtShortTime(a.time)}</span>
                    <span>·</span>
                    <span>{a.duration || 30} mins</span>
                  </div>
                </div>
                <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3}}>
                  <span style={{
                    fontSize:'0.64rem', padding:'2px 7px', borderRadius:10,
                    background: cfg.color, color:'white', fontWeight:700,
                  }}>{cfg.icon} {a.status}</span>
                  {hasNext && (
                    <button onClick={(e) => quickAdvance(a, e)}
                      style={{
                        fontSize:'0.64rem', padding:'2px 8px', borderRadius:10,
                        background:'white', color: theme.primaryDark,
                        border:`1px solid ${theme.primary}`, fontWeight:700, cursor:'pointer',
                      }} title={`Advance → ${flowLabel[a.status]}`}>
                      → {flowLabel[a.status]}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ─────────────────────────────────────────────
  //  RIGHT PANEL — pills + (selAppt details OR today list)
  // ─────────────────────────────────────────────
  const RightPanel = () => (
    <div style={{...cardStyle, position:'sticky', top: 20, padding: 16}}>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10}}>
        <h3 style={{margin:0, color: theme.navy, fontSize:'0.98rem'}}>Today</h3>
        <span style={{fontSize:'0.72rem', color:'#999'}}>
          {new Date().toLocaleDateString('en-IN', {day:'numeric', month:'short'})}
        </span>
      </div>

      <StatusPills/>

      {notCheckedIn.length > 0 && (
        <div style={{
          display:'flex', alignItems:'center', gap:8, padding:'9px 11px', borderRadius:9,
          background:'#fff4e1', color:'#7a4a00', border:'1px solid #ffd48a',
          fontSize:'0.8rem', fontWeight:600, marginBottom:12,
        }}>
          <AlertTriangle size={15} style={{flexShrink:0}}/>
          <span>{notCheckedIn.length} patient{notCheckedIn.length > 1 ? 's' : ''} checked-in nahi hua yet</span>
        </div>
      )}

      {selAppt ? (
        <>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10, paddingTop:10, borderTop:'1px solid #eef0f3'}}>
            <div>
              <h4 style={{margin:0, color: theme.navy, fontSize:'1rem'}}>{selAppt.patientName}</h4>
              <div style={{fontSize:'0.8rem', color:'#666', marginTop:3}}>{selAppt.date} · {fmtShortTime(selAppt.time)}</div>
              {selAppt.treatment && <div style={{fontSize:'0.8rem', color:'#0582ca', marginTop:2}}>🦷 {selAppt.treatment}</div>}
              {selAppt.phone && <div style={{fontSize:'0.78rem', color:'#999', marginTop:2}}><Phone size={10} style={{verticalAlign:'middle', marginRight:3}}/>{selAppt.phone}</div>}
              {(selAppt.doctor || selAppt.doctorName) && (
                <div style={{fontSize:'0.78rem', color:'#7a5cb1', marginTop:2}}><Stethoscope size={11} style={{verticalAlign:'middle', marginRight:3}}/>{selAppt.doctor || selAppt.doctorName}</div>
              )}
            </div>
            <button onClick={() => setSelAppt && setSelAppt(null)}
              style={{background:'none', border:'none', cursor:'pointer', fontSize:'1.1rem', color:'#aaa'}}>✕</button>
          </div>

          <div style={{fontSize:'0.7rem', color:'#999', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:6, fontWeight:700}}>
            Change Stage
          </div>
          <div style={{display:'flex', flexDirection:'column', gap:6}}>
            {Object.entries(STATUS_CONFIG).map(([status, cfg]) => {
              const active = selAppt.status === status;
              return (
                <button key={status} onClick={() => changeStatus(selAppt._id, status)}
                  style={{
                    padding:'8px 12px', borderRadius:8,
                    border: `2px solid ${active ? cfg.color : '#eef0f3'}`,
                    cursor:'pointer', fontWeight:700, fontSize:'0.82rem', textAlign:'left',
                    backgroundColor: active ? cfg.color : 'white',
                    color: active ? 'white' : cfg.color, transition:'0.15s',
                  }}>
                  {cfg.icon} &nbsp;{status}
                  {active && <span style={{float:'right'}}>✓</span>}
                </button>
              );
            })}
          </div>
        </>
      ) : (
        <div style={{paddingTop:10, borderTop:'1px solid #eef0f3'}}>
          <div style={{fontSize:'0.72rem', color:'#999', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:8, fontWeight:700}}>
            Appointments ({todayAppts.length})
          </div>
          <TodayList/>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────
  //  TIMELINE GRID — used by Day & Week views
  // ─────────────────────────────────────────────
  const renderTimeline = (dates) => {
    const isWeek = dates.length > 1;
    const colTemplate = `60px repeat(${dates.length}, 1fr)`;
    const showsToday = dates.includes(todayStr);

    return (
      <div ref={showsToday ? timelineScrollerRef : null}
        style={{overflowY:'auto', maxHeight:'85vh', overflowX: isWeek ? 'auto' : 'visible'}}>
        {/* Header row with dates */}
        <div style={{
          display:'grid', gridTemplateColumns: colTemplate, gap: 0,
          position:'sticky', top: 0, zIndex: 10,
          background:'white', borderBottom:'2px solid #8b94a0', paddingBottom: 8, marginBottom: 2,
          minWidth: isWeek ? 620 : 'auto',
        }}>
          <div/>
          {dates.map(ds => {
            const d = new Date(ds + 'T00:00:00');
            const isToday = ds === todayStr;
            return (
              <div key={ds} style={{textAlign:'center', padding:'4px 2px'}}>
                {isWeek && (
                  <div style={{fontSize:'0.72rem', color: isToday ? theme.primary : '#999', fontWeight:700}}>
                    {DAYS_SHORT[d.getDay()]}
                  </div>
                )}
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', margin: '2px auto 0',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  background: isToday ? theme.primary : 'transparent',
                  color: isToday ? 'white' : '#333',
                  fontWeight: 700, fontSize: isWeek ? '0.95rem' : '1.1rem',
                  boxShadow: isToday ? `0 2px 8px ${theme.primary}55` : 'none',
                }}>
                  {d.getDate()}
                </div>
                {isToday && <div style={{fontSize:'0.62rem', color: theme.primary, fontWeight:700, marginTop:2}}>TODAY</div>}
              </div>
            );
          })}
        </div>

        {/* Slot rows */}
        <div style={{minWidth: isWeek ? 620 : 'auto'}}>
          {slots.map(slot => {
            const [, minPart] = slot.split(':');
            const isHour = minPart === '00';
            const isCurrent = dates.includes(todayStr) && slot === nowSlot;
            return (
              <div key={slot} style={{
                display:'grid', gridTemplateColumns: colTemplate,
                borderBottom: isHour ? '1.5px solid #b0b8c1' : '1px dashed #c5ccd4',
                minHeight: rowHeight,
                background: isCurrent ? '#fffbe8' : 'transparent',
              }}>
                <div style={{
                  fontSize: compactMode ? '0.64rem' : '0.7rem',
                  color: isCurrent ? '#e67e00' : '#bbb',
                  textAlign:'right', paddingRight: 8, paddingTop: 3,
                  fontWeight: isCurrent ? 700 : 500,
                }}>
                  {isHour ? slot : ''}
                </div>

                {dates.map(ds => {
                  const cellAppts = (slotMap[ds] || {})[slot] || [];
                  const isEmpty = cellAppts.length === 0;
                  const isToday = ds === todayStr;
                  const working = isWorkingSlot(ds, slot, branchHours);
                  // Closed slots: hatched pattern + softer cursor + tooltip.
                  // Click still works — staff can squeeze in urgent cases
                  // after hours (matches existing ClinicHoursEditor design).
                  const idleBg = !working
                    ? CLOSED_STRIPES
                    : (isToday && isEmpty ? '#fafffd' : 'transparent');

                  return (
                    <div key={ds}
                      onClick={() => openBookingForSlot(ds, slot)}
                      title={!working ? 'Outside working hours — click to override' : undefined}
                      onMouseEnter={(e) => { if (isEmpty && working) e.currentTarget.style.background = '#e7f5ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = idleBg; }}
                      style={{
                        borderLeft:'1px solid #cdd4da', padding:'2px 4px',
                        cursor: working ? 'pointer' : 'cell',
                        background: idleBg,
                        transition: 'background 0.12s',
                        minWidth: 0,
                        overflow: 'hidden',
                      }}>
                      {cellAppts.map(a => <ApptCard key={a._id} a={a}/>)}
                      {isEmpty && <div style={{height: rowHeight - 4}}/>}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div style={{display:'flex', flexWrap:'wrap', gap:14, marginTop:14, padding:'10px 4px 4px', fontSize:'0.74rem', borderTop:'1px solid #f0f2f4'}}>
          {Object.entries(STATUS_CONFIG).map(([s, c]) => (
            <span key={s} style={{display:'flex', alignItems:'center', gap:5, color:'#666', fontWeight:600}}>
              <span style={{width:11, height:11, borderRadius:'50%', background:c.color}}/>
              {s}
            </span>
          ))}
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────
  //  NAV BUTTONS HELPERS
  // ─────────────────────────────────────────────
  const navBtn = {
    background: 'white', border: '1px solid #dee2e6', borderRadius: 9,
    padding: '7px 14px', cursor: 'pointer', fontSize: '1.05rem', color: '#555',
    transition: '0.15s',
  };
  const todayBtn = {
    padding:'6px 16px', border: `1px solid ${theme.primary}`, borderRadius: 8,
    cursor:'pointer', color: theme.primaryDark, background:'white',
    fontSize:'0.86rem', fontWeight: 700,
  };

  // ─────────────────────────────────────────────
  //  SMART-FIND DRAWER — overlaid above the view when toggled from toolbar.
  //  Rendered inside each view's wrapper so stacking context stays local.
  // ─────────────────────────────────────────────
  const SmartFindDrawer = () => {
    if (!showSmartFind) return null;
    return (
      <>
        <div onClick={() => setShowSmartFind(false)}
          style={{
            position:'fixed', inset:0, background:'rgba(15,23,42,0.38)', zIndex: 99,
          }}/>
        <div style={{
          position:'fixed', top:0, right:0, bottom:0, width:'min(460px, 96vw)',
          background:'white', zIndex:100, boxShadow:'-6px 0 24px rgba(0,0,0,0.16)',
          display:'flex', flexDirection:'column',
        }}>
          <div style={{
            padding:'14px 18px', borderBottom:'1px solid #eef0f3',
            display:'flex', justifyContent:'space-between', alignItems:'center',
            background: `linear-gradient(135deg, ${theme.primaryLight}, #ffffff)`,
          }}>
            <div style={{display:'flex', alignItems:'center', gap:8}}>
              <Sparkles size={20} color={theme.primary}/>
              <div>
                <strong style={{color: theme.navy, fontSize:'1.04rem', display:'block'}}>Smart Find Slot</strong>
                <span style={{fontSize:'0.76rem', color:'#6c757d'}}>Best slots ranked by duration, schedule & preferences</span>
              </div>
            </div>
            <button onClick={() => setShowSmartFind(false)}
              style={{background:'none', border:'none', cursor:'pointer', color:'#6c757d'}}>
              <X size={22}/>
            </button>
          </div>
          <div style={{flex:1, overflowY:'auto', padding:'14px 18px'}}>
            <SmartFindPanel
              API_BASE={API_BASE}
              patientsList={patientsList}
              treatmentTypes={treatmentTypes}
              onPick={handleSmartPick}
              initialDate={calDayDate || todayStr}
            />
          </div>
        </div>
      </>
    );
  };

  // ─────────────────────────────────────────────
  //  DAY VIEW
  // ─────────────────────────────────────────────
  if (calView === 'day') {
    const safeDate = calDayDate || todayStr;
    const shiftDay = (delta) => {
      const d = new Date(safeDate + 'T00:00:00');
      d.setDate(d.getDate() + delta);
      setCalDayDate && setCalDayDate(toISO(d));
    };
    const dispDate = new Date(safeDate + 'T00:00:00').toLocaleDateString('en-IN',
      {weekday:'long', day:'numeric', month:'long', year:'numeric'});
    const dayCount = (apptsByDate[safeDate] || []).length;

    return (
      <div>
        <Toolbar/>
        {showWaitList ? (
          <WaitListPanel API_BASE={API_BASE} />
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'1fr 310px', gap:20, alignItems:'start'}}>
            <div style={{...cardStyle, padding:18}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14}}>
                <button onClick={() => shiftDay(-1)} style={navBtn} title="Previous day">
                  <ChevronLeft size={18} style={{verticalAlign:'-3px'}}/>
                </button>
                <div style={{textAlign:'center'}}>
                  <div style={{fontWeight:700, color: theme.navy, fontSize:'1.05rem'}}>{dispDate}</div>
                  <div style={{fontSize:'0.8rem', color:'#999', marginTop:2}}>
                    {dayCount} appointment{dayCount !== 1 ? 's' : ''}
                  </div>
                </div>
                <button onClick={() => shiftDay(1)} style={navBtn} title="Next day">
                  <ChevronRight size={18} style={{verticalAlign:'-3px'}}/>
                </button>
              </div>
              <div style={{display:'flex', gap:10, marginBottom:14, alignItems:'center'}}>
                <button onClick={() => setCalDayDate && setCalDayDate(todayStr)} style={todayBtn}>Today</button>
                <input type="date" value={safeDate} onChange={e => setCalDayDate && setCalDayDate(e.target.value)}
                  style={{...inputStyle, width:'auto', padding:'6px 10px'}}/>
              </div>
              {renderTimeline([safeDate])}
            </div>
            <RightPanel/>
          </div>
        )}
        <SmartFindDrawer/>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  //  WEEK VIEW
  // ─────────────────────────────────────────────
  if (calView === 'week') {
    const safeStart = calWeekStart || (() => {
      const d = new Date();
      d.setDate(d.getDate() - d.getDay()); // Sunday
      return toISO(d);
    })();
    const weekStartDate = new Date(safeStart + 'T00:00:00');
    const weekDates = Array.from({length: 7}, (_, i) => {
      const d = new Date(weekStartDate); d.setDate(d.getDate() + i); return toISO(d);
    });
    const shiftWeek = (delta) => {
      const d = new Date(weekStartDate); d.setDate(d.getDate() + delta * 7);
      setCalWeekStart && setCalWeekStart(toISO(d));
    };
    const goThisWeek = () => {
      const d = new Date(); d.setDate(d.getDate() - d.getDay());
      setCalWeekStart && setCalWeekStart(toISO(d));
    };
    const wEnd = new Date(weekStartDate); wEnd.setDate(wEnd.getDate() + 6);
    const weekLabel = `${weekStartDate.toLocaleDateString('en-IN', {day:'numeric', month:'short'})} – ${wEnd.toLocaleDateString('en-IN', {day:'numeric', month:'short', year:'numeric'})}`;
    const weekTotal = weekDates.reduce((s, ds) => s + (apptsByDate[ds] || []).length, 0);

    return (
      <div>
        <Toolbar/>
        {showWaitList ? (
          <WaitListPanel API_BASE={API_BASE} />
        ) : (
          <div style={{display:'grid', gridTemplateColumns:'1fr 310px', gap:20, alignItems:'start'}}>
            <div style={{...cardStyle, padding:18}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14}}>
                <button onClick={() => shiftWeek(-1)} style={navBtn} title="Previous week">
                  <ChevronLeft size={18} style={{verticalAlign:'-3px'}}/>
                </button>
                <div style={{textAlign:'center'}}>
                  <div style={{fontWeight:700, color: theme.navy, fontSize:'1.05rem'}}>{weekLabel}</div>
                  <div style={{fontSize:'0.8rem', color:'#999', marginTop:2}}>
                    {weekTotal} appointment{weekTotal !== 1 ? 's' : ''} this week
                  </div>
                </div>
                <button onClick={() => shiftWeek(1)} style={navBtn} title="Next week">
                  <ChevronRight size={18} style={{verticalAlign:'-3px'}}/>
                </button>
              </div>
              <div style={{marginBottom:14}}>
                <button onClick={goThisWeek} style={todayBtn}>This Week</button>
              </div>
              {renderTimeline(weekDates)}
            </div>
            <RightPanel/>
          </div>
        )}
        <SmartFindDrawer/>
      </div>
    );
  }

  // ─────────────────────────────────────────────
  //  MONTH VIEW
  // ─────────────────────────────────────────────
  const safeMonth = (calMonth ?? today.getMonth());
  const safeYear = (calYear ?? today.getFullYear());
  const firstDayOfWeek = new Date(safeYear, safeMonth, 1).getDay();
  const daysInMonth = new Date(safeYear, safeMonth + 1, 0).getDate();
  const monthCells = [];
  for (let i = 0; i < firstDayOfWeek; i++) monthCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) monthCells.push(d);

  const prevMonth = () => {
    if (safeMonth === 0) { setCalMonth && setCalMonth(11); setCalYear && setCalYear(y => (y ?? today.getFullYear()) - 1); }
    else setCalMonth && setCalMonth(safeMonth - 1);
  };
  const nextMonth = () => {
    if (safeMonth === 11) { setCalMonth && setCalMonth(0); setCalYear && setCalYear(y => (y ?? today.getFullYear()) + 1); }
    else setCalMonth && setCalMonth(safeMonth + 1);
  };
  const goThisMonth = () => {
    setCalMonth && setCalMonth(today.getMonth());
    setCalYear && setCalYear(today.getFullYear());
  };

  if (showWaitList) {
    return (<div><Toolbar/><WaitListPanel API_BASE={API_BASE} /><SmartFindDrawer/></div>);
  }

  return (
    <div>
      <Toolbar/>
      <div style={{display:'grid', gridTemplateColumns:'1fr 310px', gap:20, alignItems:'start'}}>
        <div style={{...cardStyle, padding:18}}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18}}>
            <button onClick={prevMonth} style={navBtn} title="Previous month">
              <ChevronLeft size={18} style={{verticalAlign:'-3px'}}/>
            </button>
            <div style={{textAlign:'center'}}>
              <h2 style={{color: theme.navy, margin:0, fontSize:'1.25rem'}}>{MONTHS[safeMonth]} {safeYear}</h2>
              <button onClick={goThisMonth} style={{...todayBtn, marginTop:8, padding:'4px 14px', fontSize:'0.78rem'}}>
                Jump to current month
              </button>
            </div>
            <button onClick={nextMonth} style={navBtn} title="Next month">
              <ChevronRight size={18} style={{verticalAlign:'-3px'}}/>
            </button>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:5, marginBottom:6}}>
            {DAYS_SHORT.map(d => (
              <div key={d} style={{textAlign:'center', fontSize:'0.78rem', color:'#999', fontWeight:700, padding:'7px 0'}}>{d}</div>
            ))}
          </div>

          <div style={{display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:5}}>
            {monthCells.map((d, i) => {
              if (!d) return <div key={`pad-${i}`}/>;
              const dateStr = `${safeYear}-${String(safeMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const dayAppts = apptsByDate[dateStr] || [];
              const isToday = d === today.getDate() && safeMonth === today.getMonth() && safeYear === today.getFullYear();
              const isSelected = dateStr === selectedDate;

              return (
                <div key={dateStr}
                  onClick={() => {
                    setSelectedDate && setSelectedDate(dateStr);
                    openBookingForSlot(dateStr, '09:00');
                  }}
                  style={{
                    minHeight: 72, padding: 7, borderRadius: 9, cursor: 'pointer',
                    background: isSelected ? theme.navy : isToday ? '#e7f5ff' : '#fafbfc',
                    border: isSelected ? `2px solid ${theme.navy}` :
                            isToday ? `2px solid ${theme.primary}` : '1px solid #eef0f3',
                    transition: '0.15s',
                  }}>
                  <div style={{
                    fontWeight: isToday ? 800 : 600,
                    color: isSelected ? 'white' : isToday ? theme.primaryDark : '#333',
                    fontSize: '0.92rem',
                  }}>
                    {d}
                  </div>
                  {dayAppts.length > 0 && (
                    <div style={{marginTop: 5, display:'flex', flexDirection:'column', gap: 2}}>
                      {dayAppts.slice(0, 2).map((a, idx) => {
                        const cfg = STATUS_CONFIG[a.status] || STATUS_CONFIG['Scheduled'];
                        return (
                          <div key={idx}
                            onClick={(e) => showApptDetails(e, a)}
                            style={{
                              fontSize:'0.66rem', padding:'2px 5px', borderRadius:4,
                              whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                              background: cfg.color, color:'white', fontWeight:600,
                            }}
                            title={`${a.time} ${a.patientName}`}>
                            {a.time} {a.patientName}
                          </div>
                        );
                      })}
                      {dayAppts.length > 2 && (
                        <div style={{fontSize:'0.66rem', color: isSelected ? '#cbd5e0' : '#999', fontWeight:600}}>
                          +{dayAppts.length - 2} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{display:'flex', flexWrap:'wrap', gap:14, marginTop:18, paddingTop:14, fontSize:'0.74rem', borderTop:'1px solid #f0f2f4'}}>
            {Object.entries(STATUS_CONFIG).map(([s, c]) => (
              <span key={s} style={{display:'flex', alignItems:'center', gap:5, color:'#666', fontWeight:600}}>
                <span style={{width:11, height:11, borderRadius:'50%', background:c.color}}/>
                {s}
              </span>
            ))}
          </div>
        </div>
        <RightPanel/>
      </div>
      <SmartFindDrawer/>
    </div>
  );
};

export default CalendarPage;
